import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import { ApiResponse, PaginatedResponse, ApiError } from '@/types'
import { storage } from '@/utils'

// API Configuration - Using proxy for development
const API_BASE_URL = '/api/v1'

// Debug logging
console.log('🌐 Environment VITE_API_URL:', import.meta.env.VITE_API_URL)
console.log('🔗 Resolved API_BASE_URL:', API_BASE_URL)
console.log('🔄 Using Vite proxy for API calls')

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add auth token and timing
api.interceptors.request.use(
  (config) => {
    const token = storage.getToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    
    // Add request start time for debugging (using any to avoid TS issues)
    ;(config as any).requestStartTime = Date.now()
    
    console.log(`📤 [${new Date().toISOString()}] API Request:`, {
      method: config.method?.toUpperCase(),
      url: config.url,
      timeout: config.timeout,
      hasAuth: !!token
    })
    
    return config
  },
  (error) => {
    console.error(`💥 Request interceptor error:`, error)
    return Promise.reject(error)
  }
)

// Response interceptor to handle errors and token refresh
api.interceptors.response.use(
  (response: AxiosResponse) => {
    const duration = (response.config as any).requestStartTime
      ? Date.now() - (response.config as any).requestStartTime
      : 'unknown'
    
    console.log(`✅ [${new Date().toISOString()}] API Success:`, {
      method: response.config.method?.toUpperCase(),
      url: response.config.url,
      status: response.status,
      duration: `${duration}ms`
    })
    
    return response
  },
  async (error) => {
    const originalRequest = error.config
    const timestamp = new Date().toISOString()
    const duration = (originalRequest as any)?.requestStartTime
      ? Date.now() - (originalRequest as any).requestStartTime
      : 'unknown'
    
    // Enhanced logging for debugging ETIMEDOUT issues
    console.error(`🚨 [${timestamp}] API Error:`, {
      url: originalRequest?.url,
      method: originalRequest?.method,
      status: error.response?.status,
      statusText: error.response?.statusText,
      code: error.code,
      message: error.message,
      timeout: originalRequest?.timeout,
      duration: `${duration}ms`,
      hasResponse: !!error.response,
      hasRequest: !!error.request,
      isTimeout: error.code === 'ETIMEDOUT' || error.message?.includes('timeout'),
      retryAttempt: originalRequest._retry ? 'YES' : 'NO'
    })

    // Handle 401 errors (unauthorized)
    if (error.response?.status === 401 && !originalRequest._retry) {
      console.log(`🔄 [${timestamp}] Attempting token refresh for 401 error`)
      originalRequest._retry = true

      try {
        const refreshToken = storage.getRefreshToken()
        if (refreshToken) {
          console.log(`🔑 [${timestamp}] Refresh token found, attempting refresh`)
          const refreshStart = Date.now()
          
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refresh_token: refreshToken,
          })

          const refreshDuration = Date.now() - refreshStart
          console.log(`✅ [${timestamp}] Token refresh successful in ${refreshDuration}ms`)

          const { access_token } = response.data
          storage.setToken(access_token)

          // Retry the original request with new token
          originalRequest.headers.Authorization = `Bearer ${access_token}`
          console.log(`🔄 [${timestamp}] Retrying original request with new token`)
          return api(originalRequest)
        } else {
          console.log(`❌ [${timestamp}] No refresh token found, redirecting to login`)
        }
      } catch (refreshError: any) {
        console.error(`💥 [${timestamp}] Token refresh failed:`, {
          status: refreshError.response?.status,
          message: refreshError.message,
          code: refreshError.code
        })
        
        // Refresh failed, redirect to login
        storage.removeToken()
        storage.removeRefreshToken()
        storage.remove('user')
        window.location.href = '/auth/login'
        return Promise.reject(refreshError)
      }
    }

    // Log timeout errors specifically
    if (error.code === 'ETIMEDOUT' || error.message?.includes('timeout')) {
      console.error(`⏰ [${timestamp}] TIMEOUT ERROR DETAILS:`, {
        url: originalRequest?.url,
        method: originalRequest?.method,
        timeout: originalRequest?.timeout,
        duration: 'unknown',
        userAgent: navigator.userAgent,
        connectionType: (navigator as any).connection?.effectiveType || 'unknown'
      })
    }

    return Promise.reject(error)
  }
)

// Generic API methods
export const apiClient = {
  get: async <T = any>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<T> => {
    const response = await api.get(url, config)
    console.log(`🔍 API GET ${url} response:`, response.data)
    return response.data
  },

  post: async <T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<T> => {
    const response = await api.post(url, data, config)
    console.log(`📤 API POST ${url} response:`, response.data)
    return response.data
  },

  put: async <T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<T> => {
    const response = await api.put(url, data, config)
    console.log(`🔄 API PUT ${url} response:`, response.data)
    return response.data
  },

  patch: async <T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<T> => {
    const response = await api.patch(url, data, config)
    console.log(`🔧 API PATCH ${url} response:`, response.data)
    return response.data
  },

  delete: async <T = any>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<T> => {
    const response = await api.delete(url, config)
    console.log(`🗑️ API DELETE ${url} response:`, response.data)
    return response.data
  },

  // Paginated requests
  getPaginated: async <T = any>(
    url: string,
    params?: Record<string, any>,
    config?: AxiosRequestConfig
  ): Promise<PaginatedResponse<T>> => {
    const response = await api.get(url, { ...config, params })
    console.log(`📄 API GET PAGINATED ${url} response:`, response.data)
    return response.data
  },

  // File upload
  upload: async <T = any>(
    url: string,
    file: File,
    onUploadProgress?: (progressEvent: any) => void
  ): Promise<T> => {
    const formData = new FormData()
    formData.append('file', file)

    const response = await api.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress,
    })
    console.log(`📁 API UPLOAD ${url} response:`, response.data)
    return response.data
  },

  // Multiple files upload
  uploadFiles: async <T = any>(
    url: string,
    files: File[],
    fieldName: string = 'files',
    onUploadProgress?: (progressEvent: any) => void
  ): Promise<T> => {
    const formData = new FormData()
    files.forEach((file) => {
      formData.append(fieldName, file)
    })

    const response = await api.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress,
    })
    console.log(`📁 API UPLOAD FILES ${url} response:`, response.data)
    return response.data
  },

  // Download file
  download: async (
    url: string,
    filename?: string,
    config?: AxiosRequestConfig
  ): Promise<void> => {
    const response = await api.get(url, {
      ...config,
      responseType: 'blob',
    })

    const blob = new Blob([response.data])
    const downloadUrl = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = downloadUrl
    link.download = filename || 'download'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(downloadUrl)
  },
}

// Auth API
export const authApi = {
  login: (credentials: { email: string; password: string }) =>
    apiClient.post('/auth/login', credentials),

  register: (data: {
    email: string
    password: string
    firstName: string
    lastName: string
    businessName?: string
  }) => apiClient.post('/auth/register', data),

  logout: () => apiClient.post('/auth/logout'),

  refreshToken: (refreshToken: string) =>
    apiClient.post('/auth/refresh', { refresh_token: refreshToken }),

  forgotPassword: (email: string) =>
    apiClient.post('/auth/forgot-password', { email }),

  resetPassword: (token: string, password: string) =>
    apiClient.post('/auth/reset-password', { token, password }),

  verifyEmail: (token: string) =>
    apiClient.post('/auth/verify-email', { token }),

  validate: () => apiClient.post('/auth/validate'),
}

// Users API
export const usersApi = {
  getProfile: () => apiClient.get('/users/profile'),
  updateProfile: (data: any) => apiClient.patch('/users/profile', data),
  updatePreferences: (preferences: any) =>
    apiClient.patch('/users/preferences', preferences),
  changePassword: (data: {
    currentPassword: string
    newPassword: string
  }) => apiClient.patch('/users/change-password', data),
  deleteAccount: () => apiClient.delete('/users/account'),
}

// Materials API
export const materialsApi = {
  getAll: (params?: any) => apiClient.get('/materials', { params }),
  getById: (id: string) => apiClient.get(`/materials/${id}`),
  create: (data: any) => apiClient.post('/materials', data),
  update: (id: string, data: any) => apiClient.patch(`/materials/${id}`, data),
  delete: (id: string) => apiClient.delete(`/materials/${id}`),
  uploadImage: (id: string, file: File) =>
    apiClient.upload(`/materials/${id}/image`, file),
  getCategories: () => apiClient.get('/materials/categories'),
  getLowStock: () => apiClient.get('/materials/low-stock'),
  getPurchaseHistory: (id: string) => apiClient.get(`/materials/${id}/purchases`),
  getStockAdjustments: (materialId: string) => apiClient.get(`/materials/stock-adjustments?materialId=${materialId}`),
}

// Inventory API
export const inventoryApi = {
  getPurchases: (params?: any) => apiClient.getPaginated('/inventory/purchases', params),
  createPurchase: (data: any) => apiClient.post('/inventory/purchases', data),
  updatePurchase: (id: string, data: any) =>
    apiClient.patch(`/inventory/purchases/${id}`, data),
  deletePurchase: (id: string) => apiClient.delete(`/inventory/purchases/${id}`),
  getBatches: (params?: any) => apiClient.getPaginated('/inventory/batches', params),
  getBatch: (id: string) => apiClient.get(`/inventory/batches/${id}`),
  updateBatch: (id: string, data: any) =>
    apiClient.patch(`/inventory/batches/${id}`, data),
  getInventoryValue: () => apiClient.get('/inventory/value'),
  getStockLevels: () => apiClient.get('/inventory/stock-levels'),
}

// Projects API
export const projectsApi = {
  getAll: (params?: any) => apiClient.get('/projects', { params }),
  getById: (id: string) => apiClient.get(`/projects/${id}`),
  create: (data: any) => apiClient.post('/projects', data),
  update: (id: string, data: any) => apiClient.patch(`/projects/${id}`, data),
  delete: (id: string) => apiClient.delete(`/projects/${id}`),
  addMaterial: (id: string, data: any) =>
    apiClient.post(`/projects/${id}/materials`, data),
  updateMaterial: (id: string, materialId: string, data: any) =>
    apiClient.patch(`/projects/${id}/materials/${materialId}`, data),
  removeMaterial: (id: string, materialId: string) =>
    apiClient.delete(`/projects/${id}/materials/${materialId}`),
  calculateCost: (id: string) => apiClient.post(`/projects/${id}/calculate-cost`),
  uploadImage: (id: string, file: File) =>
    apiClient.upload(`/projects/${id}/image`, file),
}

// Time Tracking API
export const timeTrackingApi = {
  getEntries: (params?: any) => apiClient.getPaginated('/time-tracking', params),
  getEntry: (id: string) => apiClient.get(`/time-tracking/${id}`),
  createEntry: (data: any) => apiClient.post('/time-tracking', data),
  updateEntry: (id: string, data: any) =>
    apiClient.patch(`/time-tracking/${id}`, data),
  deleteEntry: (id: string) => apiClient.delete(`/time-tracking/${id}`),
  startTimer: (data: any) => apiClient.post('/time-tracking/timer/start', data),
  stopTimer: () => apiClient.post('/time-tracking/timer/stop'),
  getActiveTimer: () => apiClient.get('/time-tracking/active'),
  getTimeStats: (params?: any) => apiClient.get('/time-tracking/statistics', { params }),
}

// Reports API
export const reportsApi = {
  getAll: (params?: any) => apiClient.getPaginated('/reports', params),
  getById: (id: string) => apiClient.get(`/reports/${id}`),
  create: (data: any) => apiClient.post('/reports', data),
  update: (id: string, data: any) => apiClient.patch(`/reports/${id}`, data),
  delete: (id: string) => apiClient.delete(`/reports/${id}`),
  generate: (id: string) => apiClient.post(`/reports/${id}/generate`),
  export: (id: string, format: string) =>
    apiClient.post(`/reports/${id}/export`, { format }),
  download: (id: string) => apiClient.download(`/reports/${id}/download`),
  getTemplates: () => apiClient.get('/reports/templates/available'),
  createFromTemplate: (templateId: string, data: any) =>
    apiClient.post(`/reports/templates/${templateId}/create`, data),
  
  // Export APIs for different formats
  exportQuickBooks: async (options: {
    format: 'json' | 'csv';
    startDate?: string;
    endDate?: string;
    includeProjects?: boolean;
    includePurchases?: boolean;
    includeTimeTracking?: boolean;
  }) => {
    const response = await api.post('/reports/export/quickbooks', options, {
      responseType: 'blob'
    });
    
    // Create download
    const blob = new Blob([response.data]);
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = `quickbooks_export_${Date.now()}.${options.format}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  },
  
  exportTax: async (options: {
    format: 'json' | 'csv';
    startDate?: string;
    endDate?: string;
    includeProjects?: boolean;
    includePurchases?: boolean;
  }) => {
    const response = await api.post('/reports/export/tax', options, {
      responseType: 'blob'
    });
    
    // Create download
    const blob = new Blob([response.data]);
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = `tax_export_${Date.now()}.${options.format}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  },
  
  exportAnalytics: async (options: {
    format: 'json' | 'csv' | 'pdf' | 'excel';
    startDate?: string;
    endDate?: string;
  }) => {
    const response = await api.post('/reports/analytics/summary', options, {
      responseType: 'blob'
    });
    
    // Create download
    const blob = new Blob([response.data]);
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = `analytics_export_${Date.now()}.${options.format}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  },
}

// Charts API
export const chartsApi = {
  getAll: (params?: any) => apiClient.getPaginated('/charts', params),
  getById: (id: string) => apiClient.get(`/charts/${id}`),
  create: (data: any) => apiClient.post('/charts', data),
  update: (id: string, data: any) => apiClient.patch(`/charts/${id}`, data),
  delete: (id: string) => apiClient.delete(`/charts/${id}`),
  getData: (id: string) => apiClient.get(`/charts/${id}/data`),
  refresh: (id: string) => apiClient.post(`/charts/${id}/refresh`),
  getTemplates: () => apiClient.get('/charts/templates/prebuilt'),
  createFromTemplate: (templateId: string, data: any) =>
    apiClient.post(`/charts/templates/${templateId}/create`, data),
}

// Dashboard API
export const dashboardApi = {
  getWidgets: () => apiClient.get('/dashboard/widgets'),
  createWidget: (data: any) => apiClient.post('/dashboard/widgets', data),
  updateWidget: (id: string, data: any) =>
    apiClient.patch(`/dashboard/widgets/${id}`, data),
  deleteWidget: (id: string) => apiClient.delete(`/dashboard/widgets/${id}`),
  updateLayout: (layout: any) => apiClient.patch('/dashboard/layout', { layout }),
  getStats: () => apiClient.get('/dashboard/analytics/overview'),
  getRecentActivity: () => apiClient.get('/dashboard/recent-activity'),
}

// Notifications API
export const notificationsApi = {
  getAll: (params?: any) => apiClient.getPaginated('/notifications', params),
  markAsRead: (id: string) => apiClient.patch(`/notifications/${id}/read`),
  markAllAsRead: () => apiClient.patch('/notifications/mark-all-read'),
  dismiss: (id: string) => apiClient.patch(`/notifications/${id}/dismiss`),
  getPreferences: () => apiClient.get('/notifications/preferences'),
  updatePreferences: (preferences: any) =>
    apiClient.patch('/notifications/preferences', preferences),
  getUnreadCount: () => apiClient.get('/notifications/unread-count'),
}

// Security API
export const securityApi = {
  getAuditLogs: (params?: any) => apiClient.getPaginated('/security/audit-logs', params),
  getSecuritySummary: (days?: number) =>
    apiClient.get('/security/security-summary', { params: { days } }),
  getBackupConfigs: (params?: any) =>
    apiClient.getPaginated('/security/backup-configs', params),
  createBackupConfig: (data: any) => apiClient.post('/security/backup-configs', data),
  executeBackup: (id: string) =>
    apiClient.post(`/security/backup-configs/${id}/execute`),
  getHealthCheck: () => apiClient.get('/security/health-check'),
}

// Templates API
export const templatesApi = {
  // Get all available templates
  getAll: (params?: any) => apiClient.get('/templates', { params }),
  
  // Get all templates with basic info only (optimized for settings page)
  getAllBasic: () => apiClient.get('/templates/basic'),
  
  // Get all templates with preview data (optimized)
  getBulkPreview: () => apiClient.get('/templates/bulk-preview'),
  
  // Get template by ID
  getById: (id: string) => apiClient.get(`/templates/${id}`),
  
  // Get user's current templates
  getUserTemplates: () => apiClient.get('/templates/user/templates'),
  
  // Get user's default template
  getUserDefaultTemplate: () => apiClient.get('/templates/user/default'),
  
  // Get user's default template with custom business name (lightweight for sidebar)
  getUserDefaultTemplateWithCustomName: () => apiClient.get('/templates/user/default-with-custom-name'),
  
  // Get user's categories from current template
  getUserCategories: () => apiClient.get('/templates/user/categories'),
  
  // Get user's project types from current template
  getUserProjectTypes: () => apiClient.get('/templates/user/project-types'),
  
  // Get user's activity types from current template
  getUserActivityTypes: () => apiClient.get('/templates/user/activity-types'),
  
  // Get user's unit types from current template
  getUserUnitTypes: () => apiClient.get('/templates/user/unit-types'),
  
  // Get user's financial config from current template
  getUserFinancialConfig: () => apiClient.get('/templates/user/financial-config'),
  
  // Assign template to user (for new users or switching templates)
  assignToUser: (templateId: string, data?: any) =>
    apiClient.post(`/templates/${templateId}/assign`, data),
  
  // Switch user's template (for existing users)
  switchTemplate: (templateId: string, options?: {
    preserveData?: boolean;
    migrationStrategy?: 'merge' | 'replace' | 'backup';
  }) => apiClient.post(`/templates/${templateId}/switch`, options),
  
  // Get template preview (categories, project types, etc. without assigning)
  getPreview: (templateId: string) => apiClient.get(`/templates/${templateId}/preview`),
  
  // Search templates by industry, complexity, etc.
  search: (params: {
    industry?: string;
    complexity?: string;
    businessType?: string;
    tags?: string[];
    search?: string;
  }) => apiClient.get('/templates/search', { params }),
  
  // Get template statistics and usage info
  getStats: (templateId: string) => apiClient.get(`/templates/${templateId}/stats`),
  
  // Create custom template (for advanced users)
  createCustom: (data: any) => apiClient.post('/templates/custom', data),
  
  // Update user template overrides
  updateOverrides: (overrides: any) => apiClient.patch('/templates/user/overrides', overrides),
  
  // Get user template overrides
  getUserOverrides: () => apiClient.get('/templates/user/overrides'),
  
  // Reset user template to defaults
  resetToDefaults: (templateId: string) =>
    apiClient.post(`/templates/${templateId}/reset-defaults`),

  // Update user business template (including custom business name)
  updateUserTemplate: (templateId: string, data: any) =>
    apiClient.patch(`/templates/${templateId}/update`, data),

  // Update custom business name for a template
  updateCustomBusinessName: (templateId: string, customBusinessName: string | null) =>
    apiClient.patch(`/templates/${templateId}/custom-name`, { customBusinessName }),
}

// Error handler
export const handleApiError = (error: any): ApiError => {
  if (error.response) {
    // Server responded with error status
    return {
      message: error.response.data?.message || 'Server error',
      statusCode: error.response.status,
      error: error.response.data?.error,
      details: error.response.data?.details,
    }
  } else if (error.request) {
    // Network error
    return {
      message: 'Network error. Please check your connection.',
      statusCode: 0,
      error: 'NETWORK_ERROR',
    }
  } else {
    // Other error
    return {
      message: error.message || 'An unexpected error occurred',
      statusCode: 0,
      error: 'UNKNOWN_ERROR',
    }
  }
}

// Create authService with additional methods
export const authService = {
  ...authApi,
  getProfile: () => authApi.validate(),
}

export default api